/**
 * 复习 Store — 管理复习会话
 *
 * 对齐 features.md 7.2-7.7：复习卡片模式 + 四档评分
 */

import { create } from 'zustand';
import type { KnowledgeItem } from '@hump/shared-types';
import type { Card } from 'ts-fsrs';
import { knowledgeRepository } from '../lib/repository/knowledge';
import { newCard, reviewCard, isDue, type SrsGrade } from '../lib/srs';

interface ReviewState {
  queue: KnowledgeItem[];      // 待复习队列
  current: KnowledgeItem | null; // 当前卡片
  flipped: boolean;             // 是否翻面
  cards: Record<string, Card>;  // 每个知识点的 SRS 卡片数据
  reviewedCount: number;
  totalCount: number;

  startReview: () => Promise<void>;
  flip: () => void;
  grade: (g: SrsGrade) => Promise<void>;
  loadCards: () => Promise<void>;
}

const SRS_STORAGE_KEY = 'hump-srs-cards';

async function loadSrsCards(): Promise<Record<string, Card>> {
  try {
    const { get: idbGet } = await import('idb-keyval');
    return (await idbGet<Record<string, Card>>(SRS_STORAGE_KEY)) ?? {};
  } catch {
    return {};
  }
}

async function saveSrsCards(cards: Record<string, Card>): Promise<void> {
  const { set: idbSet } = await import('idb-keyval');
  await idbSet(SRS_STORAGE_KEY, cards);
}

export const useReviewStore = create<ReviewState>((set, get) => ({
  queue: [],
  current: null,
  flipped: false,
  cards: {},
  reviewedCount: 0,
  totalCount: 0,

  loadCards: async () => {
    const cards = await loadSrsCards();
    set({ cards });
  },

  startReview: async () => {
    const items = await knowledgeRepository.getByType('word');
    const cards = get().cards;

    // 筛选待复习：有 SRS 卡片且 due <= now，或无 SRS 卡片的新词
    const due = items.filter((item) => {
      const card = cards[item.id];
      return isDue(card);
    });

    if (due.length === 0) {
      set({ queue: [], current: null, reviewedCount: 0, totalCount: 0 });
      return;
    }

    set({
      queue: due,
      current: due[0],
      flipped: false,
      reviewedCount: 0,
      totalCount: due.length,
    });
  },

  flip: () => set({ flipped: true }),

  grade: async (g) => {
    const { current, cards } = get();
    if (!current) return;

    // 更新 SRS 卡片
    const oldCard = cards[current.id] ?? newCard();
    const newCardData = reviewCard(oldCard, g);
    const updatedCards = { ...cards, [current.id]: newCardData };
    await saveSrsCards(updatedCards);

    // 从队列移除当前
    const { queue, reviewedCount } = get();
    const next = queue.slice(1);

    set({
      cards: updatedCards,
      queue: next,
      current: next[0] ?? null,
      flipped: false,
      reviewedCount: reviewedCount + 1,
    });
  },
}));
